import { Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout, AuthLayout } from '../layouts';
import { TicketingApp, TicketDetailsPage } from '@/features/ticketing';
import { LandingPage, PrivacyPolicy, DeleteAccount, TermsOfService, ContactPage } from '@/features/landing-page';
import { UserManagementPage } from '@/features/user-management';
import { LoginPage, SetPasswordPage } from '@/features/authentication';

// RBAC & Guards
import { useAuthStore } from '@/shared/stores/authStore';
import { RoleGuard } from '@/shared/components/guards/RoleGuard';

// Feature Pages
import { SuperAdminDashboard, ListSchools, CreateSchool, SchoolDetails, ListContacts } from '@/features/super-admin/pages';
import { SchoolAdminDashboard, ListDepartments, CreateDepartment, ListEmployees, AddEmployee, ListParents, AddParent, ListStudents, ListSubDepartments, AddSubDepartment, ListDepartmentLevels, AddDepartmentLevel, AssignEmployeeToLevel, Tags, Classes, Workflows, CreateWorkflow, WorkflowVisualizer, SchoolSettings, EmailSettings } from '@/features/school-admin/pages';
import { EmployeeDashboard } from '@/features/employee/pages';
import { UnauthorizedPage } from '@/features/error-pages/pages';
import { KnowledgeHub } from '@/features/knowledge-hub';
import { Notifications } from '@/features/notifications';


/**
 * App Routes
 * Centralized route definitions with RBAC and layouts
 */
export const AppRoutes = () => {
    return (
        <Routes>
            {/* Public Landing - shown to logged out users at root */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/contact" element={<ContactPage />} />

            {/* Public Legal Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/delete-account" element={<DeleteAccount />} />

            {/* Public/Auth Routes */}
            <Route element={<AuthLayout />}>
                <Route path="/login" element={<LoginPage />} />
                {/* <Route path="/set-password" element={<SetPasswordPage />} /> */}
                {/* <Route path="/set-password/" element={<SetPasswordPage />} /> */}
                <Route path="/set-password/*" element={<SetPasswordPage />} />
                {/* <Route path="/set-password/:token" element={<SetPasswordPage />} /> */}
            </Route>

            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            {/* Protected Routes */}
            <Route element={<MainLayout />}>
                {/* 1. Super Admin Routes */}
                <Route element={<RoleGuard allowedRoles={['SUPER_ADMIN']} />}>
                    <Route path="/super-admin" element={<SuperAdminDashboard />} />
                    <Route path="/super-admin/tenants" element={<ListSchools />} />
                    <Route path="/super-admin/tenants/create" element={<CreateSchool />} />
                    <Route path="/super-admin/tenants/:schoolSlug" element={<SchoolDetails />} />
                    <Route path="/super-admin/contacts" element={<ListContacts />} />
                </Route>

                {/* 2. School Admin Routes */}
                <Route element={<RoleGuard allowedRoles={['SCHOOL_ADMIN']} />}>
                    <Route path="/:tenantSlug/school-admin" element={<SchoolAdminDashboard />} />
                    <Route path="/:tenantSlug/school-admin/departments" element={<ListDepartments />} />
                    <Route path="/:tenantSlug/school-admin/departments/create" element={<CreateDepartment />} />
                    <Route path="/:tenantSlug/school-admin/departments/:departmentId/sub-departments" element={<ListSubDepartments />} />
                    <Route path="/:tenantSlug/school-admin/departments/:departmentId/sub-departments/add" element={<AddSubDepartment />} />
                    <Route path="/:tenantSlug/school-admin/departments/:departmentId/sub-departments/:typeId/levels" element={<ListDepartmentLevels />} />
                    <Route path="/:tenantSlug/school-admin/departments/:departmentId/sub-departments/:typeId/levels/add" element={<AddDepartmentLevel />} />
                    <Route path="/:tenantSlug/school-admin/departments/:departmentId/sub-departments/:typeId/levels/:levelId/assign" element={<AssignEmployeeToLevel />} />
                    <Route path="/:tenantSlug/school-admin/employees" element={<ListEmployees />} />
                    <Route path="/:tenantSlug/school-admin/employees/invite" element={<AddEmployee />} />
                    <Route path="/:tenantSlug/school-admin/parents" element={<ListParents />} />
                    <Route path="/:tenantSlug/school-admin/parents/invite" element={<AddParent />} />
                    <Route path="/:tenantSlug/school-admin/students" element={<ListStudents />} />
                    <Route path="/:tenantSlug/school-admin/tags" element={<Tags />} />
                    <Route path="/:tenantSlug/school-admin/classes" element={<Classes />} />
                    <Route path="/:tenantSlug/school-admin/workflows" element={<Workflows />} />
                    <Route path="/:tenantSlug/school-admin/workflows/create" element={<CreateWorkflow />} />
                    <Route path="/:tenantSlug/school-admin/workflows/:workflowId/view" element={<WorkflowVisualizer />} />
                    <Route path="/:tenantSlug/school-admin/knowledge-hub" element={<KnowledgeHub />} />
                    <Route path="/:tenantSlug/school-admin/settings" element={<SchoolSettings />} />
                    <Route path="/:tenantSlug/school-admin/email-settings" element={<EmailSettings />} />
                </Route>


                {/* 3. Employee Routes */}
                <Route element={<RoleGuard allowedRoles={['EMPLOYEE']} />}>
                    <Route path="/employee" element={<EmployeeDashboard />} />
                </Route>

                {/* 4. Common Protected Routes (Ticketing, etc.) */}
                <Route element={<RoleGuard allowedRoles={['SUPER_ADMIN', 'SCHOOL_ADMIN', 'EMPLOYEE']} />}>
                    <Route path="/dashboard-redirect" element={<DashboardRedirect />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/:tenantSlug/tickets" element={<TicketingApp />} />
                    <Route path="/:tenantSlug/tickets/:ticketId" element={<TicketDetailsPage />} />
                    <Route path="/users" element={<UserManagementPage />} />
                    <Route path="/settings" element={<div>Settings Page (Placeholder)</div>} />
                </Route>
            </Route>

            <Route path="*" element={<div>404 - Page Not Found</div>} />
        </Routes>
    );
};

/**
 * Root path handler — shows LandingPage for logged-out users,
 * redirects authenticated users to their role-specific dashboard.
 */
const RootRedirect = () => {
    const { user, tenant } = useAuthStore();
    const normalizedRole = user?.role?.toUpperCase();

    if (!user) return <LandingPage />;

    const slug = tenant?.slug || user?.school?.slug || 'school';
    if (normalizedRole === 'SUPER_ADMIN') return <Navigate to="/super-admin" replace />;
    if (normalizedRole === 'SCHOOL_ADMIN') return <Navigate to={`/${slug}/school-admin`} replace />;
    if (normalizedRole === 'EMPLOYEE') return <Navigate to="/employee" replace />;

    return <LandingPage />;
};

/**
 * Helper component to redirect users to their role-specific dashboard
 */
const DashboardRedirect = () => {
    const { user, tenant } = useAuthStore();

    const normalizedRole = user?.role?.toUpperCase();
    const slug = tenant?.slug || user?.school?.slug || 'school';

    if (normalizedRole === 'SUPER_ADMIN') return <Navigate to="/super-admin" replace />;
    if (normalizedRole === 'SCHOOL_ADMIN') return <Navigate to={`/${slug}/school-admin`} replace />;
    if (normalizedRole === 'EMPLOYEE') return <Navigate to="/employee" replace />;

    return <Navigate to="/login" replace />;
};
